import type { WorkspaceServerService } from '@affine/core/modules/cloud/services/workspace-server';
import { WorkspaceService } from '@affine/core/modules/workspace/services/workspace';
import { workspacePermissionCacheOwnerKey } from '@affine/core/modules/workspace/utils/permission-cache-identity';
import {
  isBackendKnownUnavailable,
  onBackendAvailable,
} from '@affine/core/utils/backend-availability';
import { LiveData, Service } from '@toeverything/infra';
import type { DBSchema, IDBPDatabase } from 'idb';
import { openDB } from 'idb';
import { nanoid } from 'nanoid';
import { Subject } from 'rxjs';

import {
  type OfflineMutationExecutor,
  type OfflineMutationInput,
  type OfflineMutationOperation,
  type OfflineMutationOutboxStorage,
  type OfflineMutationRecord,
  type OfflineMutationScope,
  offlineMutationScopeKey,
} from '../types';

const DB_NAME = 'streamlain-offline-mutation-outbox';
const STORE_NAME = 'mutations';
const DEFAULT_MAX_RETRY_ATTEMPTS = 10;
const DEFAULT_MAX_RETRY_DELAY_MS = 5 * 60 * 1000;

interface OfflineMutationOutboxDB extends DBSchema {
  mutations: {
    key: string;
    value: OfflineMutationRecord;
    indexes: {
      scopeKey: string;
      idempotencyKey: string;
    };
  };
}

export class OfflineMutationTransientError extends Error {
  readonly kind = 'transient';
}

export class OfflineMutationDomainError extends Error {
  readonly kind = 'domain';
}

export interface OfflineMutationOutboxServiceOptions {
  storage?: OfflineMutationOutboxStorage;
  autoFlush?: boolean;
  now?: () => number;
  createId?: () => string;
  maxRetryAttempts?: number;
  maxRetryDelayMs?: number;
  retryDelayMs?: number;
}

export class MemoryOfflineMutationOutboxStorage implements OfflineMutationOutboxStorage {
  private readonly records = new Map<string, OfflineMutationRecord>();

  async listPending(
    scope: OfflineMutationScope
  ): Promise<OfflineMutationRecord[]> {
    const scopeKey = offlineMutationScopeKey(scope);
    return [...this.records.values()]
      .filter(
        record =>
          record.scopeKey === scopeKey &&
          (record.status === 'pending' || record.status === 'flushing')
      )
      .sort(compareMutations);
  }

  async listFailed(
    scope: OfflineMutationScope
  ): Promise<OfflineMutationRecord[]> {
    const scopeKey = offlineMutationScopeKey(scope);
    return [...this.records.values()]
      .filter(
        record => record.scopeKey === scopeKey && record.status === 'failed'
      )
      .map(cloneMutation)
      .sort(compareMutations);
  }

  async listAll(scope: OfflineMutationScope): Promise<OfflineMutationRecord[]> {
    const scopeKey = offlineMutationScopeKey(scope);
    return [...this.records.values()]
      .filter(record => record.scopeKey === scopeKey)
      .map(cloneMutation)
      .sort(compareMutations);
  }

  async findPendingByIdempotencyKey(
    scope: OfflineMutationScope,
    idempotencyKey: string
  ): Promise<OfflineMutationRecord | null> {
    const scopeKey = offlineMutationScopeKey(scope);
    return (
      [...this.records.values()].find(
        record =>
          record.scopeKey === scopeKey &&
          record.idempotencyKey === idempotencyKey &&
          (record.status === 'pending' || record.status === 'flushing')
      ) ?? null
    );
  }

  async upsert(mutation: OfflineMutationRecord): Promise<void> {
    this.records.set(mutation.id, cloneMutation(mutation));
  }

  async update(mutation: OfflineMutationRecord): Promise<void> {
    await this.upsert(mutation);
  }

  async remove(id: string): Promise<void> {
    this.records.delete(id);
  }

  async clearScope(scope: OfflineMutationScope): Promise<void> {
    const scopeKey = offlineMutationScopeKey(scope);
    for (const record of this.records.values()) {
      if (record.scopeKey === scopeKey) {
        this.records.delete(record.id);
      }
    }
  }

  debugRecords(): OfflineMutationRecord[] {
    return [...this.records.values()].map(cloneMutation).sort(compareMutations);
  }
}

export class IndexedDBOfflineMutationOutboxStorage implements OfflineMutationOutboxStorage {
  private dbPromise: Promise<IDBPDatabase<OfflineMutationOutboxDB>> | null =
    null;

  private get db(): Promise<IDBPDatabase<OfflineMutationOutboxDB>> {
    this.dbPromise ??= openDB<OfflineMutationOutboxDB>(DB_NAME, 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, {
            keyPath: 'id',
          });
          store.createIndex('scopeKey', 'scopeKey');
          store.createIndex('idempotencyKey', 'idempotencyKey');
        }
      },
    });
    return this.dbPromise;
  }

  async listPending(
    scope: OfflineMutationScope
  ): Promise<OfflineMutationRecord[]> {
    const db = await this.db;
    const scopeKey = offlineMutationScopeKey(scope);
    const records = await db.getAllFromIndex(STORE_NAME, 'scopeKey', scopeKey);
    return records
      .filter(
        record => record.status === 'pending' || record.status === 'flushing'
      )
      .map(cloneMutation)
      .sort(compareMutations);
  }

  async listFailed(
    scope: OfflineMutationScope
  ): Promise<OfflineMutationRecord[]> {
    const db = await this.db;
    const scopeKey = offlineMutationScopeKey(scope);
    const records = await db.getAllFromIndex(STORE_NAME, 'scopeKey', scopeKey);
    return records
      .filter(record => record.status === 'failed')
      .map(cloneMutation)
      .sort(compareMutations);
  }

  async listAll(scope: OfflineMutationScope): Promise<OfflineMutationRecord[]> {
    const db = await this.db;
    const scopeKey = offlineMutationScopeKey(scope);
    const records = await db.getAllFromIndex(STORE_NAME, 'scopeKey', scopeKey);
    return records.map(cloneMutation).sort(compareMutations);
  }

  async findPendingByIdempotencyKey(
    scope: OfflineMutationScope,
    idempotencyKey: string
  ): Promise<OfflineMutationRecord | null> {
    const db = await this.db;
    const scopeKey = offlineMutationScopeKey(scope);
    const records = await db.getAllFromIndex(
      STORE_NAME,
      'idempotencyKey',
      idempotencyKey
    );
    return (
      records.find(
        record =>
          record.scopeKey === scopeKey &&
          (record.status === 'pending' || record.status === 'flushing')
      ) ?? null
    );
  }

  async upsert(mutation: OfflineMutationRecord): Promise<void> {
    const db = await this.db;
    await db.put(STORE_NAME, cloneMutation(mutation));
  }

  async update(mutation: OfflineMutationRecord): Promise<void> {
    await this.upsert(mutation);
  }

  async remove(id: string): Promise<void> {
    const db = await this.db;
    await db.delete(STORE_NAME, id);
  }

  async clearScope(scope: OfflineMutationScope): Promise<void> {
    const db = await this.db;
    const scopeKey = offlineMutationScopeKey(scope);
    const records = await db.getAllFromIndex(STORE_NAME, 'scopeKey', scopeKey);
    const tx = db.transaction(STORE_NAME, 'readwrite');
    for (const record of records) {
      await tx.store.delete(record.id);
    }
    await tx.done;
  }

  async close(): Promise<void> {
    if (!this.dbPromise) {
      return;
    }
    const db = await this.dbPromise;
    db.close();
    this.dbPromise = null;
  }
}

export class OfflineMutationOutboxService extends Service {
  readonly failed$ = new Subject<OfflineMutationRecord>();
  readonly completed$ = new Subject<OfflineMutationRecord>();
  readonly failedMutations$ = new LiveData<OfflineMutationRecord[]>([]);
  readonly pendingMutations$ = new LiveData<OfflineMutationRecord[]>([]);

  private readonly storage: OfflineMutationOutboxStorage;
  private readonly autoFlush: boolean;
  private readonly now: () => number;
  private readonly createId: () => string;
  private readonly maxRetryAttempts: number;
  private readonly maxRetryDelayMs: number;
  private readonly retryDelayMs: number;
  private sequenceCounter = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private retryTimerDueAt: number | null = null;
  private readonly executors = new Map<
    OfflineMutationOperation,
    OfflineMutationExecutor
  >();
  private readonly flushes = new Map<string, Promise<void>>();
  private readonly canceledMutationIds = new Set<string>();

  constructor(
    private readonly workspaceService: WorkspaceService,
    private readonly workspaceServerService: WorkspaceServerService,
    options: OfflineMutationOutboxServiceOptions = {}
  ) {
    super();
    this.storage =
      options.storage ?? new IndexedDBOfflineMutationOutboxStorage();
    this.autoFlush = options.autoFlush ?? true;
    this.now = options.now ?? Date.now;
    this.createId = options.createId ?? nanoid;
    this.maxRetryAttempts = Math.max(
      1,
      options.maxRetryAttempts ?? DEFAULT_MAX_RETRY_ATTEMPTS
    );
    this.maxRetryDelayMs = Math.max(
      0,
      options.maxRetryDelayMs ?? DEFAULT_MAX_RETRY_DELAY_MS
    );
    this.retryDelayMs = Math.max(0, options.retryDelayMs ?? 1000);
    this.registerFlushTriggers();
    this.refreshMutationViews().catch(() => {});
    this.disposables.push(() => {
      if (this.retryTimer) {
        clearTimeout(this.retryTimer);
        this.retryTimer = null;
        this.retryTimerDueAt = null;
      }
    });
  }

  registerExecutor<Operation extends OfflineMutationOperation>(
    operation: Operation,
    executor: OfflineMutationExecutor<Operation>
  ): () => void {
    this.executors.set(operation, executor as OfflineMutationExecutor);
    this.scheduleFlush();
    return () => {
      if (this.executors.get(operation) === executor) {
        this.executors.delete(operation);
      }
    };
  }

  async enqueue<Operation extends OfflineMutationOperation>(
    input: OfflineMutationInput<Operation>
  ): Promise<OfflineMutationRecord<Operation>> {
    const scope = this.requireScope();
    if (input.dedupePending) {
      const existing = await this.storage.findPendingByIdempotencyKey(
        scope,
        input.idempotencyKey
      );
      if (existing) {
        return existing as OfflineMutationRecord<Operation>;
      }
    }
    const now = this.now();
    const mutation: OfflineMutationRecord<Operation> = {
      id:
        input.id ??
        (input.dedupePending
          ? dedupedMutationId(scope, input.idempotencyKey)
          : this.createId()),
      workspaceId: scope.workspaceId,
      cacheOwnerKey: scope.cacheOwnerKey,
      scopeKey: offlineMutationScopeKey(scope),
      operation: input.operation,
      payload: input.payload,
      idempotencyKey: input.idempotencyKey,
      status: 'pending',
      attempts: 0,
      createdAt: now,
      sequence: ++this.sequenceCounter,
      updatedAt: now,
    };
    await this.storage.upsert(mutation);
    await this.refreshPendingMutations();
    this.scheduleFlush();
    return mutation;
  }

  async flush(forceAfterReconnect = false): Promise<void> {
    const scope = this.requireScope();
    const scopeKey = offlineMutationScopeKey(scope);
    const existing = this.flushes.get(scopeKey);
    if (existing) {
      return existing;
    }

    const flush = this.flushScope(scope, forceAfterReconnect).finally(() => {
      if (this.flushes.get(scopeKey) === flush) {
        this.flushes.delete(scopeKey);
      }
    });
    this.flushes.set(scopeKey, flush);
    return flush;
  }

  async refreshFailedMutations(): Promise<OfflineMutationRecord[]> {
    const failed = await this.storage.listFailed(this.requireScope());
    this.failedMutations$.setValue(failed);
    return failed;
  }

  async refreshPendingMutations(): Promise<OfflineMutationRecord[]> {
    const pending = await this.storage.listPending(this.requireScope());
    this.pendingMutations$.setValue(pending);
    return pending;
  }

  async refreshMutationViews(): Promise<void> {
    await Promise.all([
      this.refreshPendingMutations(),
      this.refreshFailedMutations(),
    ]);
  }

  async retryFailed(id?: string): Promise<void> {
    const failed = await this.storage.listFailed(this.requireScope());
    const now = this.now();
    for (const mutation of failed) {
      if (id && mutation.id !== id) {
        continue;
      }
      await this.storage.update({
        ...mutation,
        status: 'pending',
        attempts: 0,
        updatedAt: now,
        lastError: undefined,
      });
    }
    this.clearRetryTimer();
    await this.refreshMutationViews();
    this.scheduleFlush();
  }

  async removeFailed(id: string): Promise<boolean> {
    const failed = await this.storage.listFailed(this.requireScope());
    const mutation = failed.find(mutation => mutation.id === id);
    if (!mutation) {
      return false;
    }
    await this.storage.remove(mutation.id);
    await this.refreshFailedMutations();
    return true;
  }

  async removePendingWhere(
    predicate: (mutation: OfflineMutationRecord) => boolean
  ): Promise<number> {
    const pending = await this.storage.listPending(this.requireScope());
    let removed = 0;
    for (const mutation of pending) {
      if (!predicate(mutation)) {
        continue;
      }
      if (mutation.status === 'flushing') {
        this.canceledMutationIds.add(mutation.id);
      }
      await this.storage.remove(mutation.id);
      removed += 1;
    }
    if (removed > 0) {
      await this.refreshPendingMutations();
    }
    return removed;
  }

  private async flushScope(
    scope: OfflineMutationScope,
    forceAfterReconnect: boolean
  ): Promise<void> {
    if (!this.canAttemptMutation(forceAfterReconnect)) {
      return;
    }

    while (true) {
      const mutations = await this.storage.listPending(scope);
      if (mutations.length === 0) {
        return;
      }

      let processedKnownMutation = false;
      for (const mutation of mutations) {
        if (this.consumeCanceledMutation(mutation)) {
          await this.storage.remove(mutation.id);
          processedKnownMutation = true;
          continue;
        }

        const executor = this.executors.get(mutation.operation);
        if (!executor) {
          continue;
        }
        processedKnownMutation = true;

        const retryWaitMs = forceAfterReconnect
          ? 0
          : this.retryWaitMs(mutation);
        if (retryWaitMs > 0) {
          this.scheduleRetry(retryWaitMs);
          return;
        }

        await this.storage.update({
          ...mutation,
          status: 'flushing',
          updatedAt: this.now(),
        });
        await this.refreshPendingMutations();

        try {
          await executor(mutation);
          if (this.consumeCanceledMutation(mutation)) {
            await this.storage.remove(mutation.id);
            await this.refreshPendingMutations();
            continue;
          }
          await this.storage.remove(mutation.id);
          await this.refreshPendingMutations();
          this.completed$.next(mutation);
        } catch (err) {
          if (this.consumeCanceledMutation(mutation)) {
            await this.storage.remove(mutation.id);
            await this.refreshPendingMutations();
            continue;
          }
          const updated = this.failedMutation(mutation, err);
          await this.storage.update(updated);
          await this.refreshMutationViews();
          if (updated.status === 'failed') {
            this.failed$.next(updated);
            continue;
          }
          this.scheduleRetry(this.retryDelayForAttempts(updated.attempts));
          return;
        }
      }

      if (!processedKnownMutation) {
        return;
      }
    }
  }

  private consumeCanceledMutation(mutation: OfflineMutationRecord): boolean {
    if (!this.canceledMutationIds.has(mutation.id)) {
      return false;
    }
    this.canceledMutationIds.delete(mutation.id);
    return true;
  }

  private failedMutation(
    mutation: OfflineMutationRecord,
    err: unknown
  ): OfflineMutationRecord {
    const lastError = errorMessage(err);
    const transient = isTransientMutationError(err);
    const attempts = mutation.attempts + 1;
    const retryExhausted = transient && attempts >= this.maxRetryAttempts;
    return {
      ...mutation,
      status: transient && !retryExhausted ? 'pending' : 'failed',
      attempts,
      updatedAt: this.now(),
      lastError,
    };
  }

  private retryDelayForAttempts(attempts: number): number {
    const exponent = Math.min(Math.max(0, attempts - 1), 30);
    return Math.min(this.maxRetryDelayMs, this.retryDelayMs * 2 ** exponent);
  }

  private retryWaitMs(mutation: OfflineMutationRecord): number {
    if (mutation.attempts <= 0 || !mutation.lastError) {
      return 0;
    }
    const retryAt =
      mutation.updatedAt + this.retryDelayForAttempts(mutation.attempts);
    return Math.max(0, retryAt - this.now());
  }

  private requireScope(): OfflineMutationScope {
    const workspace = this.workspaceService.workspace;
    if (!workspace || workspace.flavour === 'local') {
      throw new OfflineMutationDomainError(
        'Offline mutation outbox requires a cloud workspace'
      );
    }

    const server = this.workspaceServerService.server;
    const accountId = server?.account$?.value?.id;
    if (!server || !accountId) {
      throw new OfflineMutationTransientError(
        'No server account scope for offline mutation outbox'
      );
    }

    return {
      workspaceId: workspace.id,
      cacheOwnerKey: workspacePermissionCacheOwnerKey({
        accountId,
        serverBaseUrl: server.baseUrl,
        serverId: server.id,
      }),
    };
  }

  private scheduleFlush(forceAfterReconnect = false): void {
    if (!this.canAutoFlush(forceAfterReconnect)) {
      return;
    }
    queueMicrotask(() => {
      if (!this.canAutoFlush(forceAfterReconnect)) {
        return;
      }
      this.flush(forceAfterReconnect).catch(() => {});
    });
  }

  private scheduleRetry(delayMs: number): void {
    if (!this.canAutoFlush()) {
      return;
    }

    const dueAt = this.now() + Math.max(0, delayMs);
    if (
      this.retryTimer &&
      this.retryTimerDueAt !== null &&
      this.retryTimerDueAt <= dueAt
    ) {
      return;
    }
    if (this.retryTimer) {
      this.clearRetryTimer();
    }
    this.retryTimerDueAt = dueAt;

    this.retryTimer = setTimeout(
      () => {
        this.retryTimer = null;
        this.retryTimerDueAt = null;
        if (!this.canAutoFlush()) {
          return;
        }
        this.flush().catch(() => {});
      },
      Math.max(0, delayMs)
    );
  }

  private clearRetryTimer(): void {
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
    }
    this.retryTimer = null;
    this.retryTimerDueAt = null;
  }

  private canAutoFlush(forceAfterReconnect = false): boolean {
    if (!this.autoFlush) {
      return false;
    }
    return this.canAttemptMutation(forceAfterReconnect);
  }

  private canAttemptMutation(forceAfterReconnect = false): boolean {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      return false;
    }
    return forceAfterReconnect || !isBackendKnownUnavailable();
  }

  private registerFlushTriggers(): void {
    const refreshAndFlush = (forceAfterReconnect = false) => {
      this.refreshFailedMutations().catch(() => {});
      this.refreshPendingMutations().catch(() => {});
      this.scheduleFlush(forceAfterReconnect);
    };

    let accountSubscription: { unsubscribe(): void } | undefined;
    const bindAccountSubscription = () => {
      accountSubscription?.unsubscribe();
      accountSubscription =
        this.workspaceServerService.server?.account$.subscribe(() =>
          refreshAndFlush()
        );
      refreshAndFlush();
    };
    const serverSubscription = this.workspaceServerService.server$.subscribe(
      bindAccountSubscription
    );
    bindAccountSubscription();
    const unsubscribeBackendAvailability = onBackendAvailable(() =>
      refreshAndFlush(true)
    );
    this.disposables.push(() => serverSubscription.unsubscribe());
    this.disposables.push(() => accountSubscription?.unsubscribe());
    this.disposables.push(unsubscribeBackendAvailability);

    if (typeof window === 'undefined') {
      return;
    }

    const flushAfterReconnect = () => refreshAndFlush(true);
    const flushWhenVisible = () => refreshAndFlush();
    window.addEventListener('online', flushAfterReconnect);
    document.addEventListener('visibilitychange', flushWhenVisible);
    this.disposables.push(() => {
      window.removeEventListener('online', flushAfterReconnect);
      document.removeEventListener('visibilitychange', flushWhenVisible);
    });
  }
}

function compareMutations(
  left: OfflineMutationRecord,
  right: OfflineMutationRecord
): number {
  return (
    left.createdAt - right.createdAt ||
    (left.sequence ?? 0) - (right.sequence ?? 0) ||
    left.id.localeCompare(right.id)
  );
}

function cloneMutation<Operation extends OfflineMutationOperation>(
  mutation: OfflineMutationRecord<Operation>
): OfflineMutationRecord<Operation> {
  return {
    ...mutation,
    payload: { ...mutation.payload },
  };
}

function errorMessage(err: unknown): string {
  if (err instanceof Error) {
    return err.message;
  }
  if (typeof err === 'string') {
    return err;
  }
  return 'Unknown mutation error';
}

function isTransientMutationError(err: unknown): boolean {
  if (err instanceof OfflineMutationTransientError) {
    return true;
  }
  if (err instanceof OfflineMutationDomainError) {
    return false;
  }

  const status = errorStatus(err);
  if (status === 408 || status === 425 || status === 429) {
    return true;
  }
  if (status >= 400 && status < 500) {
    return false;
  }
  if (isDomainErrorCode(err)) {
    return false;
  }

  const message = errorMessage(err).toLowerCase();
  if (
    message.includes('permission') ||
    message.includes('forbidden') ||
    message.includes('denied') ||
    message.includes('unauthorized') ||
    message.includes('validation') ||
    message.includes('not found')
  ) {
    return false;
  }

  return true;
}

function errorStatus(err: unknown): number {
  if (!err || typeof err !== 'object') {
    return 0;
  }
  const status = (err as { status?: unknown }).status;
  if (typeof status === 'number') {
    return status;
  }
  const statusCode = (err as { statusCode?: unknown }).statusCode;
  if (typeof statusCode === 'number') {
    return statusCode;
  }
  const originalResponse = (
    err as {
      originalResponse?: { getStatus?: () => unknown };
    }
  ).originalResponse;
  const responseStatus = originalResponse?.getStatus?.();
  if (typeof responseStatus === 'number') {
    return responseStatus;
  }
  return 0;
}

function dedupedMutationId(
  scope: OfflineMutationScope,
  idempotencyKey: string
): string {
  return `dedupe:${offlineMutationScopeKey(scope)}:${idempotencyKey}`;
}

function isDomainErrorCode(err: unknown): boolean {
  if (!err || typeof err !== 'object') {
    return false;
  }
  const values = [
    (err as { code?: unknown }).code,
    (err as { type?: unknown }).type,
    (err as { name?: unknown }).name,
  ];
  return values.some(
    value =>
      typeof value === 'string' &&
      ['BAD_REQUEST', 'GRAPHQL_BAD_REQUEST'].includes(value.toUpperCase())
  );
}
